import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  TextField,
  Paper,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Chat as ChatIcon,
  Add as AddIcon,
  Stop as StopIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import * as api from '../api';
import {
  listChatConversations,
  getChatConversation,
  createChatConversation,
  updateChatConversation,
  getChatCompletionsUrl,
} from '../api';
import { ChatConversation, ChatMessage, ChatCompletionMessage } from '../models/chat';
import { getKeycloakInstance } from '../keycloak';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { selectSelectedTmAssessmentId, selectSelectedTmAssessment, setTmAssessments } from '../modules/threatModeling/store/tmAssessment/tmAssessmentSlice';
import { selectTmRisks } from '../modules/threatModeling/store/tmRisk/tmRiskSlice';
import { selectTmMitigations } from '../modules/threatModeling/store/tmMitigation/tmMitigationSlice';
import { selectTmActors } from '../modules/threatModeling/store/tmActor/tmActorSlice';
import { chatToolRegistry } from '../utils/chatTools';
import { getTmSystemPrompt } from '../modules/threatModeling/chat';
import { CHAT_MODELS, DEFAULT_MODEL } from '../config/chatModels';

interface ChatSidebarProps {
  open: boolean;
  onClose: () => void;
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({ open, onClose }) => {
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<ChatConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<'openai' | 'groq'>(
    (localStorage.getItem('chatVendor') as 'openai' | 'groq') || 'openai'
  );
  const [selectedModel, setSelectedModel] = useState<string>(
    localStorage.getItem('chatModel') || DEFAULT_MODEL
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const toolCallDepthRef = useRef<number>(0);
  const selectedAssessmentId = useAppSelector(selectSelectedTmAssessmentId);
  const selectedAssessment = useAppSelector(selectSelectedTmAssessment);
  const tmRisks = useAppSelector(selectTmRisks);
  const tmMitigations = useAppSelector(selectTmMitigations);
  const tmActors = useAppSelector(selectTmActors);
  const dispatch = useAppDispatch();

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversations and assessments on mount
  useEffect(() => {
    if (open) {
      loadConversations();
      loadAssessments();
    }
  }, [open]);

  const loadAssessments = async () => {
    try {
      const assessments = await api.listTmAssessments();
      dispatch(setTmAssessments(assessments));
    } catch (error) {
      console.error('Failed to load assessments:', error);
    }
  };

  const loadConversations = async () => {
    try {
      const convos = await listChatConversations();
      setConversations(convos);
      // Don't auto-select - let user start fresh or choose a conversation
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const selectConversation = async (id: number) => {
    try {
      const convo = await getChatConversation(id);
      setCurrentConversation(convo);
      setMessages(convo.messages || []);
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  };

  const generateConversationTitle = async (conversationId: number) => {
    try {
      // Fetch the conversation to get latest messages
      const conversation = await getChatConversation(conversationId);
      const messagesToUse = conversation.messages || [];

      // Only generate title if we have at least a user message and an assistant response
      if (messagesToUse.length < 2) return;

      // Skip if already has a proper generated title
      // Need title if: no title, "New Chat", or it's the truncated user message (first 50 chars)
      const firstUserMessage = messagesToUse.find(m => m.role === 'user');
      const isTruncatedMessage = firstUserMessage &&
                                 conversation.title === firstUserMessage.content.slice(0, 50);

      const needsTitle = !conversation.title ||
                         conversation.title === 'New Chat' ||
                         conversation.title.startsWith('New Chat') ||
                         isTruncatedMessage;
      if (!needsTitle) {
        return;
      }

      console.log('🏷️ Generating title for conversation', conversationId);

      // Use first 4 messages (user + assistant exchange) to generate title
      const contextMessages = messagesToUse.slice(0, 4);

      // Call LLM to generate a concise title
      const keycloak = getKeycloakInstance();
      const token = keycloak.token;

      const titleMessages: ChatCompletionMessage[] = [
        {
          role: 'system',
          content: 'Generate a concise, single-sentence title (max 50 characters) for this conversation. Respond with ONLY the title, no quotes or additional text.'
        },
        ...contextMessages.map(m => ({
          role: m.role,
          content: m.content
        }))
      ];

      const response = await fetch(getChatCompletionsUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          messages: titleMessages,
          stream: false,
        }),
      });

      if (!response.ok) {
        console.error('Failed to generate title:', response.status);
        return;
      }

      // Read the streaming response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let title = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('0:')) {
              try {
                const content = JSON.parse(line.slice(2));
                title += content;
              } catch (e) {
                console.error('Failed to parse title chunk:', e);
              }
            }
          }
        }
      }

      if (title.trim()) {
        // Clean up title (remove quotes if present)
        title = title.trim().replace(/^["']|["']$/g, '');

        // Truncate to 50 chars
        if (title.length > 50) {
          title = title.substring(0, 47) + '...';
        }

        console.log('🏷️ Generated title:', title);

        // Update the conversation title
        await updateChatConversation(conversationId, { title });

        // Update local state
        setConversations(prev =>
          prev.map(c => c.id === conversationId ? { ...c, title } : c)
        );

        if (currentConversation?.id === conversationId) {
          setCurrentConversation(prev => prev ? { ...prev, title } : null);
        }
      }
    } catch (error) {
      console.error('Failed to generate conversation title:', error);
      // Don't throw - title generation is non-critical
    }
  };

  const createNewConversation = async () => {
    try {
      const newConvo = await createChatConversation({ title: 'New Chat' });
      setConversations([newConvo, ...conversations]);
      setCurrentConversation(newConvo);
      setMessages([]);
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isStreaming) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setIsStreaming(true);
    toolCallDepthRef.current = 0; // Reset depth counter for new message

    // Add user message to UI immediately
    const tempUserMessage: ChatMessage = {
      id: Date.now(),
      role: 'user',
      content: userMessage,
      conversation_id: currentConversation?.id || 0,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMessage]);

    try {
      await streamChatCompletion(userMessage);
    } catch (error: any) {
      // Don't show error if user cancelled (aborted)
      if (error.name === 'AbortError') {
        console.log('Stream aborted by user');
      } else {
        console.error('Failed to send message:', error);
        // Remove the temporary user message on error
        setMessages((prev) => prev.filter((m) => m.id !== tempUserMessage.id));
      }
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  const streamChatCompletion = async (userMessage: string, toolResults?: any[], assistantToolCallMessage?: any) => {
    // Always include system prompt with current assessment context
    const apiMessages: ChatCompletionMessage[] = [];

    // Build system prompt from base instructions + module-specific prompts
    const basePrompt = `You are an AI assistant for Robust Risk, a risk management application. You have access to tools to help users manage their data.`;

    // Get module-specific prompts (currently only TM, can add more later)
    // Pass both assessment object and ID as fallback, plus current UI state
    const modulePrompts = getTmSystemPrompt(
      selectedAssessment,
      selectedAssessmentId,
      tmRisks,
      tmMitigations,
      tmActors
    );

    console.log('📋 System prompt context:', {
      selectedAssessment,
      selectedAssessmentId,
      hasAssessmentContext: !!(selectedAssessment || selectedAssessmentId),
      risksCount: tmRisks.length,
      mitigationsCount: tmMitigations.length,
      actorsCount: tmActors.length
    });

    apiMessages.push({
      role: 'system',
      content: `${basePrompt}\n\n${modulePrompts}`
    });

    // Add conversation history (excluding any old system messages)
    apiMessages.push(...messages.filter(m => m.role !== 'system').map((m) => ({ role: m.role, content: m.content })));

    // Only add user message if not empty AND not already in messages state
    // (we add it to state immediately at line 119 for UI, so avoid duplicating in API request)
    const lastMessage = messages[messages.length - 1];
    const userMessageAlreadyInState = lastMessage?.role === 'user' && lastMessage?.content === userMessage;

    if (userMessage && !userMessageAlreadyInState) {
      apiMessages.push({ role: 'user', content: userMessage });
    }

    // Add assistant message with tool_calls if provided (required by OpenAI before tool results)
    if (assistantToolCallMessage) {
      apiMessages.push(assistantToolCallMessage);
    }

    // Add tool results if this is a follow-up after tool execution
    if (toolResults) {
      // Tool results are already properly formatted with role, tool_call_id, name, content
      apiMessages.push(...toolResults);
    }

    console.log('📤 Sending chat request:', {
      messageCount: apiMessages.length,
      toolCount: chatToolRegistry.getDefinitions().length,
      assessmentId: selectedAssessmentId
    });

    // Create conversation if none exists
    let conversationId = currentConversation?.id;
    if (!conversationId) {
      const newConvo = await createChatConversation({ title: userMessage.slice(0, 50) });
      setCurrentConversation(newConvo);
      setConversations([newConvo, ...conversations]);
      conversationId = newConvo.id;
    }

    // Get tool definitions from registry
    const toolDefinitions = chatToolRegistry.getDefinitions();

    // Stream response from backend
    const keycloak = getKeycloakInstance();
    const token = keycloak.token;

    const requestBody = {
      conversation_id: conversationId,
      messages: apiMessages,
      tools: toolDefinitions,
      model: selectedModel,
      vendor: selectedVendor,
    };

    console.log('📤 Request body:', JSON.stringify(requestBody, null, 2));

    // Create abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const response = await fetch(getChatCompletionsUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
      signal: abortController.signal,
    });

    console.log('📥 Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Error response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    // Process SSE stream
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let accumulatedContent = '';
    let toolCalls: any[] = [];

    // Add assistant message placeholder
    const assistantMessage: ChatMessage = {
      id: Date.now() + 1,
      role: 'assistant',
      content: '',
      conversation_id: conversationId,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, assistantMessage]);

    if (reader) {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.trim()) continue; // Skip empty lines

          console.log('📨 Stream line:', line.substring(0, 100) + (line.length > 100 ? '...' : ''));

          if (line.startsWith('0:')) {
            // Text content
            try {
              const content = JSON.parse(line.slice(2));
              accumulatedContent += content;
              // Update assistant message with accumulated content
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMessage.id ? { ...m, content: accumulatedContent } : m
                )
              );
            } catch (e) {
              console.error('Failed to parse content:', e, line);
            }
          } else if (line.startsWith('t:')) {
            // Tool calls
            try {
              const toolData = JSON.parse(line.slice(2));
              toolCalls = toolData.tool_calls || [];
              console.log('🔧 Received tool calls:', toolCalls);

              // Update the assistant message to show tool execution
              const toolNames = toolCalls.map((tc: any) => `\`${tc.function.name}\``).join(', ');
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMessage.id
                    ? { ...m, content: `🔧 Executing tools: ${toolNames}` }
                    : m
                )
              );
            } catch (e) {
              console.error('Failed to parse tool calls:', e, line);
            }
          } else if (line.startsWith('d:')) {
            // Done/finish event
            try {
              const finishData = JSON.parse(line.slice(2));
              console.log('✅ Stream finished:', finishData);
            } catch (e) {
              console.error('Failed to parse finish event:', e, line);
            }
          } else if (line.startsWith('e:')) {
            // Error event
            try {
              const errorData = JSON.parse(line.slice(2));
              console.error('❌ Stream error:', errorData);
            } catch (e) {
              console.error('Failed to parse error event:', e, line);
            }
          } else {
            console.warn('⚠️ Unknown line format:', line);
          }
        }
      }
    }

    // Execute tool calls if any were received
    if (toolCalls.length > 0) {
      console.log('🔧 Starting tool execution...');

      // Create assistant message with tool_calls (required by OpenAI)
      const assistantToolCallMessage = {
        role: 'assistant',
        content: null,
        tool_calls: toolCalls
      };

      await handleToolCalls(toolCalls, assistantToolCallMessage);
    } else if (conversationId && accumulatedContent) {
      // Generate title asynchronously after first meaningful exchange (only when no tool calls)
      // Don't await - let it run in background
      setTimeout(() => {
        generateConversationTitle(conversationId);
      }, 1000);
    }
    // No need to reload - we've built the messages locally during streaming
  };

  const handleToolCalls = async (toolCalls: any[], assistantMessageWithToolCalls: any) => {
    console.log('🔧 Executing tool calls:', toolCalls);

    // Increment depth and check for infinite loop
    toolCallDepthRef.current += 1;
    const MAX_TOOL_CALL_DEPTH = 3;

    if (toolCallDepthRef.current > MAX_TOOL_CALL_DEPTH) {
      console.error('❌ Maximum tool call depth exceeded, stopping to prevent infinite loop');

      // Add error message to chat
      const errorMessage: ChatMessage = {
        id: Date.now() + 999,
        role: 'assistant',
        content: '⚠️ Maximum tool execution depth reached. The AI attempted too many sequential tool calls. Please try rephrasing your request or being more specific.',
        conversation_id: currentConversation?.id || 0,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      return;
    }

    const toolResults: any[] = [];

    // Execute each tool call silently (no UI updates)
    for (const toolCall of toolCalls) {
      try {
        const toolName = toolCall.function.name;
        const args = JSON.parse(toolCall.function.arguments);

        console.log(`🔧 Executing tool: ${toolName}`, args);

        // Execute tool using registry
        const result = await chatToolRegistry.execute(toolName, args, {
          assessmentId: selectedAssessmentId || undefined,
          dispatch: dispatch,
        });

        console.log(`✅ Tool ${toolName} result:`, result);

        toolResults.push({
          tool_call_id: toolCall.id,
          role: 'tool',
          name: toolName,
          content: JSON.stringify(result),
        });
      } catch (error: any) {
        console.error(`❌ Tool execution failed for ${toolCall.function.name}:`, error);
        toolResults.push({
          tool_call_id: toolCall.id,
          role: 'tool',
          name: toolCall.function.name,
          content: JSON.stringify({ success: false, error: error.message }),
        });
      }
    }

    // Continue conversation with tool results (AI will respond with final answer)
    // Include the assistant message with tool_calls so OpenAI has full context
    await streamChatCompletion('', toolResults, assistantMessageWithToolCalls);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const stopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsStreaming(false);
    }
  };

  const handleVendorChange = (vendor: 'openai' | 'groq') => {
    setSelectedVendor(vendor);
    localStorage.setItem('chatVendor', vendor);

    // Set default model for the new vendor
    const vendorModels = CHAT_MODELS.filter(m => m.vendor === vendor);
    if (vendorModels.length > 0) {
      const defaultModelForVendor = vendor === 'groq'
        ? 'llama-3.3-70b-versatile'
        : DEFAULT_MODEL;
      const modelExists = vendorModels.some(m => m.id === defaultModelForVendor);
      const newModel = modelExists ? defaultModelForVendor : vendorModels[0].id;
      setSelectedModel(newModel);
      localStorage.setItem('chatModel', newModel);
    }
  };

  const handleModelChange = (model: string) => {
    setSelectedModel(model);
    localStorage.setItem('chatModel', model);
  };

  // Filter models by selected vendor
  const availableModels = CHAT_MODELS.filter(m => m.vendor === selectedVendor);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400, md: 500 },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ChatIcon />
          <Typography variant="h6">AI Assistant</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <FormControl variant="standard" size="small" sx={{ minWidth: 90 }}>
            <InputLabel id="vendor-select-label" sx={{ fontSize: '0.875rem' }}>Vendor</InputLabel>
            <Select
              labelId="vendor-select-label"
              value={selectedVendor}
              label="Vendor"
              onChange={(e) => handleVendorChange(e.target.value as 'openai' | 'groq')}
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="openai" sx={{ fontSize: '0.875rem' }}>OpenAI</MenuItem>
              <MenuItem value="groq" sx={{ fontSize: '0.875rem' }}>Groq</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="standard" size="small" sx={{ minWidth: 130 }}>
            <InputLabel id="model-select-label" sx={{ fontSize: '0.875rem' }}>Model</InputLabel>
            <Select
              labelId="model-select-label"
              value={selectedModel}
              label="Model"
              onChange={(e) => handleModelChange(e.target.value)}
              sx={{ fontSize: '0.875rem' }}
            >
              {availableModels.map((model) => (
                <MenuItem key={model.id} value={model.id} sx={{ fontSize: '0.875rem' }}>
                  {model.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton onClick={createNewConversation} size="small" title="New conversation">
            <AddIcon />
          </IconButton>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Conversations List */}
      {conversations.length > 1 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 0.5,
              bgcolor: 'primary.dark',
              color: 'primary.contrastText',
              cursor: 'pointer',
            }}
            onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
          >
            <Typography variant="caption" sx={{ fontWeight: 'medium', color: 'inherit' }}>
              Conversation History ({conversations.length})
            </Typography>
            <IconButton size="small" sx={{ p: 0, color: 'inherit' }}>
              {isHistoryExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Box>
          <Collapse in={isHistoryExpanded}>
            <Box
              sx={{
                maxHeight: 150,
                overflowY: 'auto',
              }}
            >
              <List dense>
                {conversations.map((convo, index) => (
                  <ListItemButton
                    key={convo.id}
                    selected={currentConversation?.id === convo.id}
                    onClick={() => selectConversation(convo.id!)}
                    sx={{
                      bgcolor: index % 2 === 0 ? 'action.hover' : 'transparent',
                      '&.Mui-selected': {
                        bgcolor: 'primary.light',
                        '&:hover': {
                          bgcolor: 'primary.light',
                        },
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 2 }}>
                      <Typography
                        noWrap
                        sx={{ fontSize: '0.875rem', flex: 1, minWidth: 0 }}
                      >
                        {convo.title || 'Untitled'}
                      </Typography>
                      <Typography
                        sx={{ fontSize: '0.7rem', opacity: 0.7, whiteSpace: 'nowrap' }}
                      >
                        {convo.updated_at
                          ? new Date(convo.updated_at).toLocaleDateString([], { month: 'numeric', day: 'numeric' }) + ' ' +
                            new Date(convo.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                          : ''}
                      </Typography>
                    </Box>
                  </ListItemButton>
                ))}
              </List>
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
            <Typography>Start a conversation with the AI assistant</Typography>
          </Box>
        ) : (
          messages.map((message) => (
            <Paper
              key={message.id}
              elevation={1}
              sx={{
                p: 1.5,
                alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '85%',
                bgcolor: message.role === 'user' ? 'primary.light' : 'background.paper',
                color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                borderRadius: 3,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, gap: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.7,
                    fontWeight: 'bold',
                  }}
                >
                  {message.role === 'user' ? 'You' : 'Assistant'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.6,
                    fontSize: '0.7rem',
                  }}
                >
                  {new Date(message.created_at).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              </Box>
              {message.role === 'assistant' ? (
                <Box
                  sx={{
                    '& p': { margin: 0, marginBottom: 1 },
                    '& p:last-child': { marginBottom: 0 },
                    '& ul, & ol': { marginTop: 0.5, marginBottom: 0.5, paddingLeft: 2 },
                    '& code': {
                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      padding: '2px 4px',
                      borderRadius: 1,
                      fontSize: '0.875em',
                    },
                    '& pre': {
                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      padding: 1,
                      borderRadius: 1,
                      overflowX: 'auto',
                    },
                    '& pre code': {
                      backgroundColor: 'transparent',
                      padding: 0,
                    },
                  }}
                >
                  <ReactMarkdown>{message.content}</ReactMarkdown>
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {message.content}
                </Typography>
              )}
            </Paper>
          ))
        )}
        {isStreaming && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            onFocus={() => setIsHistoryExpanded(false)}
            placeholder="Type your message..."
            disabled={isStreaming}
            size="small"
          />
          {isStreaming ? (
            <IconButton
              color="error"
              onClick={stopStreaming}
              title="Stop generating"
            >
              <StopIcon />
            </IconButton>
          ) : (
            <IconButton
              color="primary"
              onClick={sendMessage}
              disabled={!inputValue.trim()}
              title="Send message"
            >
              <SendIcon />
            </IconButton>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};
